import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { toast } from 'sonner';
import { 
  HelpCircle, 
  Save, 
  X, 
  RotateCcw, 
  Settings2, 
  Plug2, 
  CheckCircle2, 
  AlertCircle,
  RefreshCw,
  Database,
  Info,
  FileEdit,
  ArrowRight
} from 'lucide-react';
import type { AssetOptions, NominalAccount, CostCentre } from '@/types/settings';
import type { 
  Integration, 
  IntegrationType, 
  DatabaseInstance,
  IntegrationConfig 
} from '@/types/integrations';
import { mockIntegrations, mockDatabaseInstances } from '@/types/integrations';
import { cn } from '@/lib/utils';
import { dataService } from '@/services/data-service';
import { useIntegration } from '@/providers/IntegrationProvider';

// Mock data for nominal accounts and cost centres
const mockNominalAccounts: NominalAccount[] = [
  { code: '1000', name: 'Fixed Assets - Land & Buildings' },
  { code: '1100', name: 'Fixed Assets - Plant & Machinery' },
  { code: '1200', name: 'Fixed Assets - Vehicles' },
  { code: '1300', name: 'Fixed Assets - IT Equipment' },
  { code: '1400', name: 'Fixed Assets - Furniture & Fittings' },
  { code: '2000', name: 'Accumulated Depreciation - Buildings' },
  { code: '2100', name: 'Accumulated Depreciation - Machinery' },
  { code: '2200', name: 'Accumulated Depreciation - Vehicles' },
  { code: '3000', name: 'Depreciation Expense' },
  { code: '3100', name: 'Depreciation - Buildings' },
  { code: '3200', name: 'Depreciation - Equipment' },
  { code: '4000', name: 'Disposal of Assets' },
  { code: '4100', name: 'Gain/Loss on Disposal' },
  { code: '5000', name: 'Revaluation Reserve' },
  { code: '5100', name: 'Revaluation Surplus' },
  { code: '6000', name: 'Bank Account - Main' },
  { code: '6100', name: 'Bank Account - Disposals' },
  { code: '7000', name: 'Work in Progress' },
  { code: '7100', name: 'Capital WIP' },
];

const mockCostCentres: CostCentre[] = [
  { code: 'CC001', name: 'Head Office' },
  { code: 'CC002', name: 'Manufacturing' },
  { code: 'CC003', name: 'Sales & Marketing' },
  { code: 'CC004', name: 'IT Department' },
  { code: 'CC005', name: 'Finance' },
  { code: 'CC006', name: 'Human Resources' },
  { code: 'CC007', name: 'Research & Development' },
  { code: 'CC008', name: 'Warehouse' },
  { code: 'CC009', name: 'Customer Service' },
  { code: 'CC010', name: 'Administration' },
];

const defaultOptions: AssetOptions = {
  general: {
    assetAutoNumbering: false,
    doNotAllowNegativeBoughtValue: false,
    defaultBaseValueOn: 'remaining',
    servicePeriodDefaults: 0,
  },
  promptNominalUpdate: {
    depreciation: false,
    addition: false,
    revaluation: false,
    disposal: false,
    transfer: false,
  },
  updateNominal: {
    includeResourceOnNominalJournals: false,
    summariseJournals: true,
  },
  transactions: {
    allowZeroValueAdditions: false,
    forceAllDepreciationBeforePeriodEnd: false,
    doNotReverseInPeriodDepreciation: false,
    nominalAccounts: {
      assetCost: undefined,
      depreciationBS: undefined,
      depreciationPL: undefined,
      disposal: undefined,
      revaluationBS: undefined,
      revaluationPL: undefined,
      disposalsBankAccount: undefined,
      additionsWIPBankAcc: undefined,
    },
    costing: {
      depreciation: undefined,
      addition: undefined,
      disposals: undefined,
      revaluation: undefined,
    },
    postCostingTransactions: {
      depreciation: false,
      additions: false,
      revaluations: false,
      disposals: false,
    },
    postCreditToCosting: {
      depreciation: false,
      additions: false,
      revaluations: false,
      disposals: false,
    },
  },
  defaultSettings: {
    defaultMethod: 'Straight Line',
    defaultPercentage: 20.0,
    defaultPeriodYears: 5,
    defaultFrequency: 'Monthly',
    doNotDepreciate: false,
    forceTransfersOnChangingCategory: false,
  },
  periods: {
    periodRatios: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
};

const defaultIntegrationConfig: IntegrationConfig = {
  activeIntegration: undefined,
  integrations: {}
};

export default function AssetOptionsPage(): React.ReactElement {
  const { setSelectedInstance: setIntegrationContextInstance } = useIntegration();
  const [options, setOptions] = useState<AssetOptions>(defaultOptions);
  const [integrationConfig, setIntegrationConfig] = useState<IntegrationConfig>(defaultIntegrationConfig);
  const [integrations, setIntegrations] = useState<Integration[]>(mockIntegrations);
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationType | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadedInstances, setLoadedInstances] = useState<Record<IntegrationType, DatabaseInstance[]>>({
    'access-dimensions': [],
    'access-financials': [],
    'xero': [],
    'quickbooks': []
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [savedOptions, setSavedOptions] = useState<AssetOptions>(defaultOptions);
  const [savedIntegrationState, setSavedIntegrationState] = useState<{
    integration: IntegrationType | null;
    instance: string;
  }>({ integration: null, instance: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  // Structured change tracking for better display
  interface FieldChange {
    field: string;
    oldValue: string | null;
    newValue: string;
  }

  interface SectionChanges {
    section: string;
    changes: FieldChange[];
  }

  interface TabChanges {
    tab: string;
    sections: SectionChanges[];
  }

  const [pendingChanges, setPendingChanges] = useState<TabChanges[]>([]);

  useEffect(() => {
    // Load saved options from localStorage
    const savedOptionsStr = localStorage.getItem('assetOptions');
    if (savedOptionsStr) {
      const loadedOptions = JSON.parse(savedOptionsStr);
      setOptions(loadedOptions);
      setSavedOptions(loadedOptions);
    } else {
      setSavedOptions(defaultOptions);
    }

    const savedIntegrations = localStorage.getItem('integrationConfig');
    if (savedIntegrations) {
      const config = JSON.parse(savedIntegrations);
      setIntegrationConfig(config);
      if (config.activeIntegration) {
        setSelectedIntegration(config.activeIntegration);
        setSavedIntegrationState({
          integration: config.activeIntegration,
          instance: config.integrations[config.activeIntegration]?.instanceId || ''
        });
        const integrationData = config.integrations[config.activeIntegration];
        if (integrationData?.instanceId) {
          setSelectedInstance(integrationData.instanceId);
        }
      }
    }
  }, []);

  useEffect(() => {
    // Warn user before leaving page with unsaved changes
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleChange = (section: keyof AssetOptions, field: string, value: any) => {
    setOptions((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    setHasUnsavedChanges(true);
    setShowSuccessAlert(false);
  };

  const handleIntegrationToggle = (integrationType: IntegrationType) => {
    const integration = integrations.find(i => i.id === integrationType);
    
    if (integration?.comingSoon) {
      return;
    }

    // If trying to select a different integration when one is already active
    if (selectedIntegration && selectedIntegration !== integrationType) {
      const confirmed = window.confirm(
        `Switching integrations will disconnect your current ${integrations.find(i => i.id === selectedIntegration)?.name} integration. Do you want to continue?`
      );
      if (!confirmed) {
        return;
      }
    }

    if (selectedIntegration === integrationType) {
      // Deselecting current integration
      setSelectedIntegration(null);
      setSelectedInstance('');
    } else {
      // Selecting new integration
      setSelectedIntegration(integrationType);
      setSelectedInstance('');
      // Load instances for this integration
      loadInstances(integrationType);
    }
    
    setHasUnsavedChanges(true);
    setShowSuccessAlert(false);
  };

    const loadInstances = async (integrationType: IntegrationType) => {
      setLoadingInstances(true);
    
      // Only fetch from API for access-financials integration
      if (integrationType === 'access-financials') {
        try {
          const response = await dataService.getFinancialsDatabaseInstances(integrationType);
          if (response && response.instances) {
            setLoadedInstances(prev => ({
              ...prev,
              [integrationType]: response.instances.map((instance: any) => ({
                id: instance.id,
                name: instance.name,
                environment: instance.environment as 'production' | 'staging' | 'test' | 'development' | undefined,
                connectionStatus: instance.connectionStatus as 'active' | 'inactive' | 'error',
                lastSync: instance.lastSync,
                description: instance.description
              }))
            }));
          }
        } catch (error) {
          console.error('Failed to load database instances:', error);
          // Fall back to mock data on error
          setLoadedInstances(prev => ({
            ...prev,
            [integrationType]: mockDatabaseInstances[integrationType] || []
          }));
        }
      } else {
        // Use mock data for other integrations
        setLoadedInstances(prev => ({
          ...prev,
          [integrationType]: mockDatabaseInstances[integrationType] || []
        }));
      }
    
      setLoadingInstances(false);
    };

  const handleInstanceChange = (instanceId: string) => {
    setSelectedInstance(instanceId);
    setHasUnsavedChanges(true);
    setShowSuccessAlert(false);
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    // Validate Details tab
    if (!options.defaultSettings.defaultMethod) {
      errors.push('Default Method is required');
    }
    if (options.defaultSettings.defaultPercentage === null || options.defaultSettings.defaultPercentage === undefined) {
      errors.push('Default Percentage is required');
    } else if (options.defaultSettings.defaultPercentage < 0 || options.defaultSettings.defaultPercentage > 100) {
      errors.push('Default Percentage must be between 0 and 100');
    }
    if (options.defaultSettings.defaultPeriodYears === null || options.defaultSettings.defaultPeriodYears === undefined) {
      errors.push('Default Period (Years) is required');
    } else if (options.defaultSettings.defaultPeriodYears < 0) {
      errors.push('Default Period (Years) must be 0 or greater');
    }
    if (!options.defaultSettings.defaultFrequency) {
      errors.push('Default Frequency is required');
    }

    // Validate Integrations tab
    if (selectedIntegration && !selectedInstance) {
      errors.push('Please select a database instance for the active integration');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const generateChangeSummary = (): TabChanges[] => {
    const tabChanges: TabChanges[] = [];

    // Helper function to format boolean values
    const formatBoolean = (value: boolean) => value ? 'Enabled' : 'Disabled';

    // Details Tab Changes
    const detailsSections: SectionChanges[] = [];

    // General Settings Section
    const generalChanges: FieldChange[] = [];
    if (options.general.assetAutoNumbering !== savedOptions.general.assetAutoNumbering) {
      generalChanges.push({
        field: 'Asset Auto Numbering',
        oldValue: formatBoolean(savedOptions.general.assetAutoNumbering),
        newValue: formatBoolean(options.general.assetAutoNumbering)
      });
    }
    if (options.general.doNotAllowNegativeBoughtValue !== savedOptions.general.doNotAllowNegativeBoughtValue) {
      generalChanges.push({
        field: 'Prevent Negative Values',
        oldValue: formatBoolean(savedOptions.general.doNotAllowNegativeBoughtValue),
        newValue: formatBoolean(options.general.doNotAllowNegativeBoughtValue)
      });
    }
    if (options.general.defaultBaseValueOn !== savedOptions.general.defaultBaseValueOn) {
      generalChanges.push({
        field: 'Default Base Value On',
        oldValue: savedOptions.general.defaultBaseValueOn === 'remaining' ? 'Remaining Postings' : 'Original Postings',
        newValue: options.general.defaultBaseValueOn === 'remaining' ? 'Remaining Postings' : 'Original Postings'
      });
    }
    if (options.general.servicePeriodDefaults !== savedOptions.general.servicePeriodDefaults) {
      generalChanges.push({
        field: 'Service Period Defaults',
        oldValue: savedOptions.general.servicePeriodDefaults.toString(),
        newValue: options.general.servicePeriodDefaults.toString()
      });
    }
    if (generalChanges.length > 0) {
      detailsSections.push({ section: 'General Settings', changes: generalChanges });
    }

    // Nominal Update Settings Section
    const nominalChanges: FieldChange[] = [];
    
    // Prompt nominal update changes
    const promptKeys = ['depreciation', 'addition', 'revaluation', 'disposal', 'transfer'] as const;
    const promptLabels = {
      depreciation: 'Prompt on Depreciation',
      addition: 'Prompt on Addition',
      revaluation: 'Prompt on Revaluation',
      disposal: 'Prompt on Disposal',
      transfer: 'Prompt on Transfer'
    };
    
    promptKeys.forEach(key => {
      if (options.promptNominalUpdate[key] !== savedOptions.promptNominalUpdate[key]) {
        nominalChanges.push({
          field: promptLabels[key],
          oldValue: formatBoolean(savedOptions.promptNominalUpdate[key]),
          newValue: formatBoolean(options.promptNominalUpdate[key])
        });
      }
    });

    // Journal settings changes
    if (options.updateNominal.includeResourceOnNominalJournals !== savedOptions.updateNominal.includeResourceOnNominalJournals) {
      nominalChanges.push({
        field: 'Include Resource on Journals',
        oldValue: formatBoolean(savedOptions.updateNominal.includeResourceOnNominalJournals),
        newValue: formatBoolean(options.updateNominal.includeResourceOnNominalJournals)
      });
    }
    if (options.updateNominal.summariseJournals !== savedOptions.updateNominal.summariseJournals) {
      nominalChanges.push({
        field: 'Summarise Journals by Default',
        oldValue: formatBoolean(savedOptions.updateNominal.summariseJournals),
        newValue: formatBoolean(options.updateNominal.summariseJournals)
      });
    }
    if (nominalChanges.length > 0) {
      detailsSections.push({ section: 'Nominal Update Settings', changes: nominalChanges });
    }

    // Transaction Settings Section
    const transactionChanges: FieldChange[] = [];
    if (options.transactions.allowZeroValueAdditions !== savedOptions.transactions.allowZeroValueAdditions) {
      transactionChanges.push({
        field: 'Allow Zero Value Additions',
        oldValue: formatBoolean(savedOptions.transactions.allowZeroValueAdditions),
        newValue: formatBoolean(options.transactions.allowZeroValueAdditions)
      });
    }
    if (options.transactions.forceAllDepreciationBeforePeriodEnd !== savedOptions.transactions.forceAllDepreciationBeforePeriodEnd) {
      transactionChanges.push({
        field: 'Force Depreciation Before Period End',
        oldValue: formatBoolean(savedOptions.transactions.forceAllDepreciationBeforePeriodEnd),
        newValue: formatBoolean(options.transactions.forceAllDepreciationBeforePeriodEnd)
      });
    }
    if (options.transactions.doNotReverseInPeriodDepreciation !== savedOptions.transactions.doNotReverseInPeriodDepreciation) {
      transactionChanges.push({
        field: 'Preserve In-Period Depreciation',
        oldValue: formatBoolean(savedOptions.transactions.doNotReverseInPeriodDepreciation),
        newValue: formatBoolean(options.transactions.doNotReverseInPeriodDepreciation)
      });
    }
    if (transactionChanges.length > 0) {
      detailsSections.push({ section: 'Transaction Settings', changes: transactionChanges });
    }

    // Default Depreciation Settings Section
    const depreciationChanges: FieldChange[] = [];
    if (options.defaultSettings.doNotDepreciate !== savedOptions.defaultSettings.doNotDepreciate) {
      depreciationChanges.push({
        field: 'Do Not Depreciate',
        oldValue: formatBoolean(savedOptions.defaultSettings.doNotDepreciate),
        newValue: formatBoolean(options.defaultSettings.doNotDepreciate)
      });
    }
    if (options.defaultSettings.defaultMethod !== savedOptions.defaultSettings.defaultMethod) {
      depreciationChanges.push({
        field: 'Default Method',
        oldValue: savedOptions.defaultSettings.defaultMethod,
        newValue: options.defaultSettings.defaultMethod
      });
    }
    if (options.defaultSettings.defaultPercentage !== savedOptions.defaultSettings.defaultPercentage) {
      depreciationChanges.push({
        field: 'Default Percentage',
        oldValue: `${savedOptions.defaultSettings.defaultPercentage}%`,
        newValue: `${options.defaultSettings.defaultPercentage}%`
      });
    }
    if (options.defaultSettings.defaultPeriodYears !== savedOptions.defaultSettings.defaultPeriodYears) {
      depreciationChanges.push({
        field: 'Default Period (Years)',
        oldValue: savedOptions.defaultSettings.defaultPeriodYears.toString(),
        newValue: options.defaultSettings.defaultPeriodYears.toString()
      });
    }
    if (options.defaultSettings.defaultFrequency !== savedOptions.defaultSettings.defaultFrequency) {
      depreciationChanges.push({
        field: 'Default Frequency',
        oldValue: savedOptions.defaultSettings.defaultFrequency,
        newValue: options.defaultSettings.defaultFrequency
      });
    }
    if (options.defaultSettings.forceTransfersOnChangingCategory !== savedOptions.defaultSettings.forceTransfersOnChangingCategory) {
      depreciationChanges.push({
        field: 'Force Transfers on Category Change',
        oldValue: formatBoolean(savedOptions.defaultSettings.forceTransfersOnChangingCategory),
        newValue: formatBoolean(options.defaultSettings.forceTransfersOnChangingCategory)
      });
    }
    if (depreciationChanges.length > 0) {
      detailsSections.push({ section: 'Default Depreciation Settings', changes: depreciationChanges });
    }

    if (detailsSections.length > 0) {
      tabChanges.push({ tab: 'Details', sections: detailsSections });
    }

    // Integrations Tab Changes
    const integrationSections: SectionChanges[] = [];
    const integrationChanges: FieldChange[] = [];

    if (selectedIntegration !== savedIntegrationState.integration) {
      const oldIntegrationName = savedIntegrationState.integration 
        ? integrations.find(i => i.id === savedIntegrationState.integration)?.name 
        : null;
      const newIntegrationName = selectedIntegration 
        ? integrations.find(i => i.id === selectedIntegration)?.name 
        : null;

      integrationChanges.push({
        field: 'Active Integration',
        oldValue: oldIntegrationName ?? null,
        newValue: newIntegrationName || 'None'
      });
    }

    if (selectedIntegration && selectedInstance !== savedIntegrationState.instance) {
      const oldInstanceName = savedIntegrationState.instance && savedIntegrationState.integration
        ? mockDatabaseInstances[savedIntegrationState.integration]?.find(i => i.id === savedIntegrationState.instance)?.name
        : null;
      const newInstanceName = mockDatabaseInstances[selectedIntegration]?.find(i => i.id === selectedInstance)?.name;

      if (newInstanceName) {
        integrationChanges.push({
          field: 'Database Instance',
          oldValue: oldInstanceName ?? null,
          newValue: newInstanceName
        });
      }
    }

    if (integrationChanges.length > 0) {
      integrationSections.push({ section: 'Financial System Integration', changes: integrationChanges });
      tabChanges.push({ tab: 'Integrations', sections: integrationSections });
    }

    return tabChanges;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    // Generate change summary and show confirmation modal
    const changes = generateChangeSummary();
    setPendingChanges(changes);
    setShowConfirmModal(true);
  };

    const handleConfirmSave = async () => {
      setIsSaving(true);
    
      try {
        // Save options
        localStorage.setItem('assetOptions', JSON.stringify(options));
        setSavedOptions(options);

        // Save integration config
        const newIntegrationConfig: IntegrationConfig = {
          activeIntegration: selectedIntegration || undefined,
          integrations: {}
        };

        if (selectedIntegration && selectedInstance) {
          // Get the instance name from loaded instances
          const instanceName = loadedInstances[selectedIntegration]?.find(
            i => i.id === selectedInstance
          )?.name || selectedInstance;

          newIntegrationConfig.integrations[selectedIntegration] = {
            enabled: true,
            instanceId: selectedInstance,
            lastSync: new Date().toISOString()
          };

          // Save to backend API and update context
          await setIntegrationContextInstance(
            selectedIntegration,
            selectedInstance,
            instanceName,
            true
          );
        }

        localStorage.setItem('integrationConfig', JSON.stringify(newIntegrationConfig));
        setIntegrationConfig(newIntegrationConfig);
        setSavedIntegrationState({
          integration: selectedIntegration,
          instance: selectedInstance
        });

        setHasUnsavedChanges(false);
        setShowConfirmModal(false);
      
        // Show success toast
        toast.success('Configuration updated successfully');
      } catch (error) {
        console.error('Failed to save integration settings:', error);
        toast.error('Failed to save integration settings. Please try again.');
      } finally {
        setIsSaving(false);
      }
    };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirmModal(true);
      return;
    }
  };

  const handleConfirmCancel = () => {
    const savedOptionsStr = localStorage.getItem('assetOptions');
    if (savedOptionsStr) {
      const loadedOptions = JSON.parse(savedOptionsStr);
      setOptions(loadedOptions);
      setSavedOptions(loadedOptions);
    } else {
      setOptions(defaultOptions);
      setSavedOptions(defaultOptions);
    }

    const savedIntegrations = localStorage.getItem('integrationConfig');
    if (savedIntegrations) {
      const config = JSON.parse(savedIntegrations);
      setIntegrationConfig(config);
      if (config.activeIntegration) {
        setSelectedIntegration(config.activeIntegration);
        const integrationData = config.integrations[config.activeIntegration];
        if (integrationData?.instanceId) {
          setSelectedInstance(integrationData.instanceId);
        }
        setSavedIntegrationState({
          integration: config.activeIntegration,
          instance: integrationData?.instanceId || ''
        });
      } else {
        setSelectedIntegration(null);
        setSelectedInstance('');
        setSavedIntegrationState({ integration: null, instance: '' });
      }
    } else {
      setIntegrationConfig(defaultIntegrationConfig);
      setSelectedIntegration(null);
      setSelectedInstance('');
      setSavedIntegrationState({ integration: null, instance: '' });
    }

    setHasUnsavedChanges(false);
    setValidationErrors([]);
    setShowCancelConfirmModal(false);
    
    toast.info('Changes discarded');
  };

  const handleResetToDefaults = () => {
    setOptions(defaultOptions);
    setHasUnsavedChanges(true);
    setShowSuccessAlert(false);
  };

  const getEnvironmentBadgeVariant = (env?: string) => {
    switch (env) {
      case 'production':
        return 'default';
      case 'staging':
        return 'secondary';
      case 'test':
        return 'outline';
      default:
        return 'outline';
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Settings2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Asset Options</h2>
              <p className="text-sm text-muted-foreground">
                Configure asset settings and financial system integrations
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area with Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Vertical Tabs Sidebar */}
        <div className="w-64 border-r bg-card" style={{ borderColor: 'oklch(0.928 0.006 264.531)' }}>
          <div className="p-4 space-y-1">
            <button
              onClick={() => setActiveTab('details')}
              className={cn(
                "flex w-full items-center justify-start gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors",
                activeTab === 'details'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Settings2 className="h-4 w-4" />
              Details
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={cn(
                "flex w-full items-center justify-start gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors",
                activeTab === 'transactions'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <FileEdit className="h-4 w-4" />
              Transactions
            </button>
            <button
              onClick={() => setActiveTab('periods')}
              className={cn(
                "flex w-full items-center justify-start gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors",
                activeTab === 'periods'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Database className="h-4 w-4" />
              Periods
            </button>
            <button
              onClick={() => setActiveTab('integrations')}
              className={cn(
                "flex w-full items-center justify-start gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors",
                activeTab === 'integrations'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Plug2 className="h-4 w-4" />
              Integrations
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-auto p-6 pb-32">
          <div className="container mx-auto max-w-7xl">
            {showSuccessAlert && (
              <Alert className="mb-6 border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertDescription className="text-green-800 dark:text-green-200">
                  {successMessage || 'Settings saved successfully!'}
                </AlertDescription>
              </Alert>
            )}

            {validationErrors.length > 0 && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <ul className="list-inside list-disc space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-6">
              {/* General Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings2 className="h-5 w-5 text-primary" />
                    General Settings
                  </CardTitle>
                  <CardDescription>
                    Configure basic asset management behaviors and defaults
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label htmlFor="assetAutoNumbering" className="text-base">
                            Asset Auto Numbering
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Automatically generate asset numbers
                          </p>
                        </div>
                        <Switch
                          id="assetAutoNumbering"
                          checked={options.general.assetAutoNumbering}
                          onCheckedChange={(checked) =>
                            handleChange('general', 'assetAutoNumbering', checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label htmlFor="doNotAllowNegativeBoughtValue" className="text-base">
                            Prevent Negative Values
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Block negative bought values for assets
                          </p>
                        </div>
                        <Switch
                          id="doNotAllowNegativeBoughtValue"
                          checked={options.general.doNotAllowNegativeBoughtValue}
                          onCheckedChange={(checked) =>
                            handleChange('general', 'doNotAllowNegativeBoughtValue', checked)
                          }
                        />
                      </div>

                      <div className="space-y-3 rounded-lg border p-4">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="servicePeriodDefaults" className="text-base">
                            Service Period Defaults
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="h-4 w-4 cursor-help text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p>
                                  Number of periods after the current period in which depreciation should start.
                                  Leave zero/blank to depreciate in the current period.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Input
                          id="servicePeriodDefaults"
                          type="number"
                          min="0"
                          value={options.general.servicePeriodDefaults}
                          onChange={(e) =>
                            handleChange('general', 'servicePeriodDefaults', parseInt(e.target.value) || 0)
                          }
                          placeholder="0"
                        />
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-3 rounded-lg border p-4">
                      <Label htmlFor="defaultBaseValueOn" className="text-base">
                        Default Base Value On
                      </Label>
                      <Select
                        value={options.general.defaultBaseValueOn}
                        onValueChange={(value: string) => handleChange('general', 'defaultBaseValueOn', value)}
                      >
                        <SelectTrigger id="defaultBaseValueOn">
                          <SelectValue placeholder="Select base value calculation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="remaining">Remaining Postings</SelectItem>
                          <SelectItem value="original">Original Postings</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground">
                        Choose how base values are calculated
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Nominal Update Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Nominal Update Settings</CardTitle>
                  <CardDescription>
                    Configure when to prompt for nominal updates and journal settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Prompt Before Period End */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="mb-1 font-medium">Prompt Nominal Update Before Period End</h4>
                      <p className="text-sm text-muted-foreground">
                        Select which transaction types should trigger a nominal update prompt
                      </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {[
                        { key: 'depreciation', label: 'Depreciation' },
                        { key: 'addition', label: 'Addition' },
                        { key: 'revaluation', label: 'Revaluation' },
                        { key: 'disposal', label: 'Disposal' },
                        { key: 'transfer', label: 'Transfer' },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center space-x-2 rounded-lg border p-3">
                          <Checkbox
                            id={`prompt${key}`}
                            checked={options.promptNominalUpdate[key as keyof typeof options.promptNominalUpdate]}
                            onCheckedChange={(checked) =>
                              handleChange('promptNominalUpdate', key, checked)
                            }
                          />
                          <Label htmlFor={`prompt${key}`} className="cursor-pointer font-normal">
                            {label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Journal Settings */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="mb-1 font-medium">Journal Settings</h4>
                      <p className="text-sm text-muted-foreground">
                        Configure how nominal journals are generated
                      </p>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label htmlFor="includeResourceOnNominalJournals" className="text-base">
                            Include Resource on Journals
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Add resource information to nominal journals
                          </p>
                        </div>
                        <Switch
                          id="includeResourceOnNominalJournals"
                          checked={options.updateNominal.includeResourceOnNominalJournals}
                          onCheckedChange={(checked) =>
                            handleChange('updateNominal', 'includeResourceOnNominalJournals', checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label htmlFor="summariseJournals" className="text-base">
                            Summarise Journals by Default
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Combine similar journal entries automatically
                          </p>
                        </div>
                        <Switch
                          id="summariseJournals"
                          checked={options.updateNominal.summariseJournals}
                          onCheckedChange={(checked) =>
                            handleChange('updateNominal', 'summariseJournals', checked)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Transaction Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Transaction Settings</CardTitle>
                  <CardDescription>
                    Configure transaction processing rules and constraints
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="allowZeroValueAdditions" className="text-base">
                        Allow Zero Value Additions
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Permit adding assets with zero initial value
                      </p>
                    </div>
                    <Switch
                      id="allowZeroValueAdditions"
                      checked={options.transactions.allowZeroValueAdditions}
                      onCheckedChange={(checked) =>
                        handleChange('transactions', 'allowZeroValueAdditions', checked)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="forceAllDepreciationBeforePeriodEnd" className="text-base">
                        Force Depreciation Before Period End
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Require all depreciation before closing periods
                      </p>
                    </div>
                    <Switch
                      id="forceAllDepreciationBeforePeriodEnd"
                      checked={options.transactions.forceAllDepreciationBeforePeriodEnd}
                      onCheckedChange={(checked) =>
                        handleChange('transactions', 'forceAllDepreciationBeforePeriodEnd', checked)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="doNotReverseInPeriodDepreciation" className="text-base">
                        Preserve In-Period Depreciation
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Don't reverse depreciation for backdated disposals
                      </p>
                    </div>
                    <Switch
                      id="doNotReverseInPeriodDepreciation"
                      checked={options.transactions.doNotReverseInPeriodDepreciation}
                      onCheckedChange={(checked) =>
                        handleChange('transactions', 'doNotReverseInPeriodDepreciation', checked)
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Default Depreciation Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Default Depreciation Settings</CardTitle>
                  <CardDescription>
                    Set default values for new asset depreciation calculations
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between space-x-2 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950">
                    <div className="space-y-0.5">
                      <Label htmlFor="doNotDepreciate" className="text-base">
                        Do Not Depreciate
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Disable depreciation for new assets by default
                      </p>
                    </div>
                    <Switch
                      id="doNotDepreciate"
                      checked={options.defaultSettings.doNotDepreciate}
                      onCheckedChange={(checked) =>
                        handleChange('defaultSettings', 'doNotDepreciate', checked)
                      }
                    />
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="defaultMethod">
                          Default Method <span className="text-destructive">*</span>
                        </Label>
                        <Select
                          value={options.defaultSettings.defaultMethod}
                          onValueChange={(value: string) => handleChange('defaultSettings', 'defaultMethod', value)}
                          disabled={options.defaultSettings.doNotDepreciate}
                        >
                          <SelectTrigger id="defaultMethod">
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Straight Line">Straight Line</SelectItem>
                            <SelectItem value="Reducing Balance">Reducing Balance</SelectItem>
                            <SelectItem value="Sum of Digits">Sum of Digits</SelectItem>
                            <SelectItem value="Period Ratio">Period Ratio</SelectItem>
                            <SelectItem value="None">None</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Depreciation calculation method for new assets
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="defaultPercentage">
                          Default Percentage <span className="text-destructive">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="defaultPercentage"
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={options.defaultSettings.defaultPercentage}
                            onChange={(e) =>
                              handleChange('defaultSettings', 'defaultPercentage', parseFloat(e.target.value))
                            }
                            disabled={options.defaultSettings.doNotDepreciate}
                            className="pr-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                            %
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Annual depreciation rate (0-100%)
                        </p>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="defaultPeriodYears">
                          Default Period (Years) <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="defaultPeriodYears"
                          type="number"
                          min="0"
                          value={options.defaultSettings.defaultPeriodYears}
                          onChange={(e) =>
                            handleChange('defaultSettings', 'defaultPeriodYears', parseInt(e.target.value) || 0)
                          }
                          disabled={options.defaultSettings.doNotDepreciate}
                        />
                        <p className="text-xs text-muted-foreground">
                          Expected useful life in years
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="defaultFrequency">
                          Default Frequency <span className="text-destructive">*</span>
                        </Label>
                        <Select
                          value={options.defaultSettings.defaultFrequency}
                          onValueChange={(value: string) => handleChange('defaultSettings', 'defaultFrequency', value)}
                          disabled={options.defaultSettings.doNotDepreciate}
                        >
                          <SelectTrigger id="defaultFrequency">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Monthly">Monthly</SelectItem>
                            <SelectItem value="Quarterly">Quarterly</SelectItem>
                            <SelectItem value="Half Yearly">Half Yearly</SelectItem>
                            <SelectItem value="Yearly">Yearly</SelectItem>
                            <SelectItem value="N/A">N/A</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          How often depreciation is calculated
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="forceTransfersOnChangingCategory" className="text-base">
                        Force Transfers on Category Change
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Require transfer transaction when changing asset category
                      </p>
                    </div>
                    <Switch
                      id="forceTransfersOnChangingCategory"
                      checked={options.defaultSettings.forceTransfersOnChangingCategory}
                      onCheckedChange={(checked) =>
                        handleChange('defaultSettings', 'forceTransfersOnChangingCategory', checked)
                      }
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions" className="space-y-6">
              {/* Nominal Accounts Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileEdit className="h-5 w-5 text-primary" />
                    Nominal Accounts
                  </CardTitle>
                  <CardDescription>
                    Configure nominal accounts for asset transactions. Accounts are fetched from the integration database if linked.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="assetCost">Asset Cost</Label>
                      <Select
                        value={options.transactions.nominalAccounts.assetCost || ''}
                                                onValueChange={(value: string) => {
                                                  const newAccounts = { ...options.transactions.nominalAccounts, assetCost: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="assetCost">
                          <SelectValue placeholder="Select nominal account" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockNominalAccounts.map((account) => (
                            <SelectItem key={account.code} value={account.code}>
                              {account.code} - {account.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="depreciationBS">Depreciation B/S</Label>
                      <Select
                        value={options.transactions.nominalAccounts.depreciationBS || ''}
                                                onValueChange={(value: string) => {
                                                  const newAccounts = { ...options.transactions.nominalAccounts, depreciationBS: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="depreciationBS">
                          <SelectValue placeholder="Select nominal account" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockNominalAccounts.map((account) => (
                            <SelectItem key={account.code} value={account.code}>
                              {account.code} - {account.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="depreciationPL">Depreciation P&L</Label>
                      <Select
                        value={options.transactions.nominalAccounts.depreciationPL || ''}
                                                onValueChange={(value: string) => {
                                                  const newAccounts = { ...options.transactions.nominalAccounts, depreciationPL: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="depreciationPL">
                          <SelectValue placeholder="Select nominal account" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockNominalAccounts.map((account) => (
                            <SelectItem key={account.code} value={account.code}>
                              {account.code} - {account.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="disposal">Disposal</Label>
                      <Select
                        value={options.transactions.nominalAccounts.disposal || ''}
                                                onValueChange={(value: string) => {
                                                  const newAccounts = { ...options.transactions.nominalAccounts, disposal: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="disposal">
                          <SelectValue placeholder="Select nominal account" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockNominalAccounts.map((account) => (
                            <SelectItem key={account.code} value={account.code}>
                              {account.code} - {account.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="revaluationBS">Revaluation B/S</Label>
                      <Select
                        value={options.transactions.nominalAccounts.revaluationBS || ''}
                                                onValueChange={(value: string) => {
                                                  const newAccounts = { ...options.transactions.nominalAccounts, revaluationBS: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="revaluationBS">
                          <SelectValue placeholder="Select nominal account" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockNominalAccounts.map((account) => (
                            <SelectItem key={account.code} value={account.code}>
                              {account.code} - {account.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="revaluationPL">Revaluation P&L</Label>
                      <Select
                        value={options.transactions.nominalAccounts.revaluationPL || ''}
                                                onValueChange={(value: string) => {
                                                  const newAccounts = { ...options.transactions.nominalAccounts, revaluationPL: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="revaluationPL">
                          <SelectValue placeholder="Select nominal account" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockNominalAccounts.map((account) => (
                            <SelectItem key={account.code} value={account.code}>
                              {account.code} - {account.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="disposalsBankAccount">Disposals Bank Account</Label>
                      <Select
                        value={options.transactions.nominalAccounts.disposalsBankAccount || ''}
                                                onValueChange={(value: string) => {
                                                  const newAccounts = { ...options.transactions.nominalAccounts, disposalsBankAccount: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="disposalsBankAccount">
                          <SelectValue placeholder="Select nominal account" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockNominalAccounts.map((account) => (
                            <SelectItem key={account.code} value={account.code}>
                              {account.code} - {account.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="additionsWIPBankAcc">Additions WIP / Bank Acc</Label>
                      <Select
                        value={options.transactions.nominalAccounts.additionsWIPBankAcc || ''}
                                                onValueChange={(value: string) => {
                                                  const newAccounts = { ...options.transactions.nominalAccounts, additionsWIPBankAcc: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="additionsWIPBankAcc">
                          <SelectValue placeholder="Select nominal account" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockNominalAccounts.map((account) => (
                            <SelectItem key={account.code} value={account.code}>
                              {account.code} - {account.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Costing Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Costing</CardTitle>
                  <CardDescription>
                    Configure cost centre codes for asset transactions. These codes will be used when posting Nominal Journals if no Cost Centre is set on the Asset. The Project Code used is always set on the Asset. These Cost Centres must exist in every Project.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="costingDepreciation">Depreciation</Label>
                      <Select
                        value={options.transactions.costing.depreciation || ''}
                                                onValueChange={(value: string) => {
                                                  const newCosting = { ...options.transactions.costing, depreciation: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, costing: newCosting }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="costingDepreciation">
                          <SelectValue placeholder="Select cost centre" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockCostCentres.map((centre) => (
                            <SelectItem key={centre.code} value={centre.code}>
                              {centre.code} - {centre.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="costingAddition">Addition</Label>
                      <Select
                        value={options.transactions.costing.addition || ''}
                                                onValueChange={(value: string) => {
                                                  const newCosting = { ...options.transactions.costing, addition: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, costing: newCosting }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="costingAddition">
                          <SelectValue placeholder="Select cost centre" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockCostCentres.map((centre) => (
                            <SelectItem key={centre.code} value={centre.code}>
                              {centre.code} - {centre.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="costingDisposals">Disposals</Label>
                      <Select
                        value={options.transactions.costing.disposals || ''}
                                                onValueChange={(value: string) => {
                                                  const newCosting = { ...options.transactions.costing, disposals: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, costing: newCosting }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="costingDisposals">
                          <SelectValue placeholder="Select cost centre" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockCostCentres.map((centre) => (
                            <SelectItem key={centre.code} value={centre.code}>
                              {centre.code} - {centre.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="costingRevaluation">Revaluation</Label>
                      <Select
                        value={options.transactions.costing.revaluation || ''}
                                                onValueChange={(value: string) => {
                                                  const newCosting = { ...options.transactions.costing, revaluation: value };
                          setOptions(prev => ({
                            ...prev,
                            transactions: { ...prev.transactions, costing: newCosting }
                          }));
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="costingRevaluation">
                          <SelectValue placeholder="Select cost centre" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockCostCentres.map((centre) => (
                            <SelectItem key={centre.code} value={centre.code}>
                              {centre.code} - {centre.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Post Costing Transactions Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Post Costing Transactions</CardTitle>
                  <CardDescription>
                    Configure which transaction types should post to costing and whether to post credit entries
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="mb-4 font-medium">Post Costing Transactions</h4>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="flex items-center space-x-2 rounded-lg border p-3">
                        <Checkbox
                          id="postCostingDepreciation"
                          checked={options.transactions.postCostingTransactions.depreciation}
                          onCheckedChange={(checked) => {
                            const newPostCosting = { ...options.transactions.postCostingTransactions, depreciation: checked as boolean };
                            setOptions(prev => ({
                              ...prev,
                              transactions: { ...prev.transactions, postCostingTransactions: newPostCosting }
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        />
                        <Label htmlFor="postCostingDepreciation" className="cursor-pointer font-normal">
                          Post Costing Transactions for Depreciation
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2 rounded-lg border p-3">
                        <Checkbox
                          id="postCostingAdditions"
                          checked={options.transactions.postCostingTransactions.additions}
                          onCheckedChange={(checked) => {
                            const newPostCosting = { ...options.transactions.postCostingTransactions, additions: checked as boolean };
                            setOptions(prev => ({
                              ...prev,
                              transactions: { ...prev.transactions, postCostingTransactions: newPostCosting }
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        />
                        <Label htmlFor="postCostingAdditions" className="cursor-pointer font-normal">
                          Post Costing Transactions for Additions
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2 rounded-lg border p-3">
                        <Checkbox
                          id="postCostingRevaluations"
                          checked={options.transactions.postCostingTransactions.revaluations}
                          onCheckedChange={(checked) => {
                            const newPostCosting = { ...options.transactions.postCostingTransactions, revaluations: checked as boolean };
                            setOptions(prev => ({
                              ...prev,
                              transactions: { ...prev.transactions, postCostingTransactions: newPostCosting }
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        />
                        <Label htmlFor="postCostingRevaluations" className="cursor-pointer font-normal">
                          Post Costing Transactions for Revaluations
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2 rounded-lg border p-3">
                        <Checkbox
                          id="postCostingDisposals"
                          checked={options.transactions.postCostingTransactions.disposals}
                          onCheckedChange={(checked) => {
                            const newPostCosting = { ...options.transactions.postCostingTransactions, disposals: checked as boolean };
                            setOptions(prev => ({
                              ...prev,
                              transactions: { ...prev.transactions, postCostingTransactions: newPostCosting }
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        />
                        <Label htmlFor="postCostingDisposals" className="cursor-pointer font-normal">
                          Post Costing Transactions for Disposals
                        </Label>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="mb-4 font-medium">Post CREDIT to Costing</h4>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="flex items-center space-x-2 rounded-lg border p-3">
                        <Checkbox
                          id="postCreditDepreciation"
                          checked={options.transactions.postCreditToCosting.depreciation}
                          onCheckedChange={(checked) => {
                            const newPostCredit = { ...options.transactions.postCreditToCosting, depreciation: checked as boolean };
                            setOptions(prev => ({
                              ...prev,
                              transactions: { ...prev.transactions, postCreditToCosting: newPostCredit }
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        />
                        <Label htmlFor="postCreditDepreciation" className="cursor-pointer font-normal">
                          Post CREDIT Depreciation to Costing
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2 rounded-lg border p-3">
                        <Checkbox
                          id="postCreditAdditions"
                          checked={options.transactions.postCreditToCosting.additions}
                          onCheckedChange={(checked) => {
                            const newPostCredit = { ...options.transactions.postCreditToCosting, additions: checked as boolean };
                            setOptions(prev => ({
                              ...prev,
                              transactions: { ...prev.transactions, postCreditToCosting: newPostCredit }
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        />
                        <Label htmlFor="postCreditAdditions" className="cursor-pointer font-normal">
                          Post CREDIT Additions to Costing
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2 rounded-lg border p-3">
                        <Checkbox
                          id="postCreditRevaluations"
                          checked={options.transactions.postCreditToCosting.revaluations}
                          onCheckedChange={(checked) => {
                            const newPostCredit = { ...options.transactions.postCreditToCosting, revaluations: checked as boolean };
                            setOptions(prev => ({
                              ...prev,
                              transactions: { ...prev.transactions, postCreditToCosting: newPostCredit }
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        />
                        <Label htmlFor="postCreditRevaluations" className="cursor-pointer font-normal">
                          Post CREDIT Revaluations to Costing
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2 rounded-lg border p-3">
                        <Checkbox
                          id="postCreditDisposals"
                          checked={options.transactions.postCreditToCosting.disposals}
                          onCheckedChange={(checked) => {
                            const newPostCredit = { ...options.transactions.postCreditToCosting, disposals: checked as boolean };
                            setOptions(prev => ({
                              ...prev,
                              transactions: { ...prev.transactions, postCreditToCosting: newPostCredit }
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        />
                        <Label htmlFor="postCreditDisposals" className="cursor-pointer font-normal">
                          Post CREDIT Disposals to Costing
                        </Label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Periods Tab */}
            <TabsContent value="periods" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-primary" />
                    Period Ratios
                  </CardTitle>
                  <CardDescription>
                    Configure the number of weeks in each period for period ratio depreciation calculations
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="rounded-lg border bg-muted/50 p-4">
                      <Label className="text-base font-medium">
                        Please enter the number of weeks in each period
                      </Label>
                      <p className="mt-1 text-sm text-muted-foreground">
                        These values are used for period ratio depreciation calculations
                      </p>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((period) => (
                        <div key={period} className="space-y-2">
                          <Label htmlFor={`period${period}`}>Period {period}</Label>
                          <Input
                            id={`period${period}`}
                            type="number"
                            min="0"
                            step="0.1"
                            value={options.periods.periodRatios[period - 1]}
                            onChange={(e) => {
                              const newRatios = [...options.periods.periodRatios];
                              newRatios[period - 1] = parseFloat(e.target.value) || 0;
                              setOptions(prev => ({
                                ...prev,
                                periods: { periodRatios: newRatios }
                              }));
                              setHasUnsavedChanges(true);
                            }}
                            placeholder="0"
                          />
                        </div>
                      ))}
                    </div>

                    <Separator />

                    <div className="flex items-center justify-between rounded-lg border bg-primary/5 p-4">
                      <div className="space-y-0.5">
                        <Label className="text-base font-medium">Total Weeks</Label>
                        <p className="text-sm text-muted-foreground">
                          Sum of all period ratios
                        </p>
                      </div>
                      <div className="text-2xl font-bold text-primary">
                        {options.periods.periodRatios.reduce((sum, val) => sum + val, 0).toFixed(1)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Integrations Tab */}
            <TabsContent value="integrations" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plug2 className="h-5 w-5 text-primary" />
                    Financial System Integrations
                  </CardTitle>
                  <CardDescription>
                    Connect your assets to a financial system to sync data and streamline reporting.
                    Only one integration can be active at a time.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {integrations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                        <Plug2 className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <h3 className="mb-2 text-lg font-semibold">No Integrations Available</h3>
                      <p className="max-w-sm text-sm text-muted-foreground">
                        Connect your assets to a financial system to sync data and streamline reporting
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Integration Cards Grid */}
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {integrations.map((integration) => {
                          const isSelected = selectedIntegration === integration.id;
                          const isDisabled = integration.comingSoon || (selectedIntegration !== null && !isSelected);

                          return (
                            <div
                              key={integration.id}
                              className={cn(
                                'relative overflow-hidden rounded-lg border-2 transition-all',
                                isSelected && 'border-primary bg-primary/5',
                                !isSelected && !isDisabled && 'border-border hover:border-primary/50',
                                isDisabled && 'opacity-60'
                              )}
                            >
                              <div className="p-6">
                                {/* Header */}
                                <div className="mb-4 flex items-start justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className={cn(
                                      'flex h-12 w-12 items-center justify-center rounded-lg overflow-hidden',
                                      isSelected ? 'bg-primary/10 ring-2 ring-primary' : 'bg-muted'
                                    )}>
                                      {integration.logo ? (
                                        <img 
                                          src={integration.logo} 
                                          alt={`${integration.name} logo`}
                                          className="h-8 w-8 object-contain"
                                          onError={(e) => {
                                            // Fallback to Database icon if image fails to load
                                            e.currentTarget.style.display = 'none';
                                            e.currentTarget.parentElement!.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-6 w-6"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M3 5V19A9 3 0 0 0 21 19V5"></path><path d="M3 12A9 3 0 0 0 21 12"></path></svg>';
                                          }}
                                        />
                                      ) : (
                                        <Database className="h-6 w-6" />
                                      )}
                                    </div>
                                    <div>
                                      <h3 className="font-semibold">{integration.name}</h3>
                                      {integration.comingSoon && (
                                        <Badge variant="secondary" className="mt-1">
                                          Coming Soon
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  <Switch
                                    checked={isSelected}
                                    onCheckedChange={() => handleIntegrationToggle(integration.id)}
                                    disabled={isDisabled}
                                  />
                                </div>

                                {/* Description */}
                                <p className="mb-4 text-sm text-muted-foreground">
                                  {integration.description}
                                </p>

                                {/* Features */}
                                {integration.features && integration.features.length > 0 && (
                                  <div className="space-y-2">
                                    <p className="text-xs font-medium text-muted-foreground">Key Features:</p>
                                    <ul className="space-y-1">
                                      {integration.features.slice(0, 3).map((feature, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                                          <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                                          <span>{feature}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>

                              {/* Instance Selection Panel */}
                              {isSelected && !integration.comingSoon && (
                                <div className="border-t bg-muted/50 p-4">
                                  <div className="space-y-3">
                                    <Label htmlFor={`instance-${integration.id}`} className="text-sm font-medium">
                                      Database Instance
                                    </Label>

                                    {loadingInstances ? (
                                      <div className="flex items-center justify-center py-4">
                                        <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                                      </div>
                                                                        ) : (loadedInstances[integration.id]?.length > 0 || mockDatabaseInstances[integration.id]?.length > 0) ? (
                                                                          <Select
                                                                            value={selectedInstance}
                                                                            onValueChange={handleInstanceChange}
                                                                          >
                                                                            <SelectTrigger id={`instance-${integration.id}`}>
                                                                              <SelectValue placeholder="Select an instance" />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                              {(loadedInstances[integration.id]?.length > 0 ? loadedInstances[integration.id] : mockDatabaseInstances[integration.id]).map((instance) => (
                                            <SelectItem key={instance.id} value={instance.id}>
                                              <div className="flex items-center gap-2">
                                                <span>{instance.name}</span>
                                                {instance.environment && (
                                                  <Badge
                                                    variant={getEnvironmentBadgeVariant(instance.environment)}
                                                    className="text-xs"
                                                  >
                                                    {instance.environment}
                                                  </Badge>
                                                )}
                                              </div>
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <Alert>
                                        <Info className="h-4 w-4" />
                                        <AlertDescription className="text-xs">
                                          No database instances found. Please configure your connection settings.
                                        </AlertDescription>
                                      </Alert>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          </div>
        </div>
      </div>

      {/* Fixed Footer - Pinned to bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-background border-t shadow-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handleResetToDefaults}
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Reset to Defaults
            </Button>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={!hasUnsavedChanges}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasUnsavedChanges}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="h-5 w-5 text-primary" />
              Confirm Configuration Changes
            </DialogTitle>
            <DialogDescription>
              Review the changes you're about to save to your asset configuration.
            </DialogDescription>
          </DialogHeader>

          {pendingChanges.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center px-6">
              <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">
                No changes detected
              </p>
            </div>
          ) : (
            <>
              <div className="px-6 pb-3 flex items-center justify-between flex-shrink-0 border-b">
                <p className="text-sm font-medium">
                  {pendingChanges.reduce((total, tab) => total + tab.sections.reduce((sum, section) => sum + section.changes.length, 0), 0)} field{pendingChanges.reduce((total, tab) => total + tab.sections.reduce((sum, section) => sum + section.changes.length, 0), 0) === 1 ? '' : 's'} modified across {pendingChanges.length} tab{pendingChanges.length === 1 ? '' : 's'}
                </p>
                <Badge variant="secondary">
                  {pendingChanges.reduce((total, tab) => total + tab.sections.reduce((sum, section) => sum + section.changes.length, 0), 0)}
                </Badge>
              </div>
              
              <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
                <div className="space-y-4">
                  {pendingChanges.map((tabChange, tabIndex) => (
                    <div key={tabIndex} className="space-y-3">
                      {/* Tab Header */}
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <Settings2 className="h-4 w-4 text-primary" />
                        <h3 className="font-semibold text-sm">{tabChange.tab}</h3>
                        <Badge variant="outline" className="ml-auto text-xs">
                          {tabChange.sections.reduce((sum, section) => sum + section.changes.length, 0)} change{tabChange.sections.reduce((sum, section) => sum + section.changes.length, 0) === 1 ? '' : 's'}
                        </Badge>
                      </div>

                      {/* Sections */}
                      <div className="space-y-3 pl-2">
                        {tabChange.sections.map((sectionChange, sectionIndex) => (
                          <div key={sectionIndex} className="space-y-2">
                            {/* Section Header */}
                            <div className="flex items-center gap-2">
                              <div className="h-px w-3 bg-border" />
                              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                {sectionChange.section}
                              </h4>
                            </div>

                            {/* Field Changes */}
                            <div className="space-y-2 pl-5">
                              {sectionChange.changes.map((fieldChange, fieldIndex) => (
                                <div
                                  key={fieldIndex}
                                  className="rounded-md border bg-card p-3 space-y-1.5"
                                >
                                  <div className="text-xs font-medium text-foreground">
                                    {fieldChange.field}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs">
                                    <div className="flex-1 flex items-center gap-1.5">
                                      <span className="text-muted-foreground">From:</span>
                                      <code className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                                        {fieldChange.oldValue || '�'}
                                      </code>
                                    </div>
                                    <ArrowRight className="h-3 w-3 text-primary flex-shrink-0" />
                                    <div className="flex-1 flex items-center gap-1.5">
                                      <span className="text-muted-foreground">To:</span>
                                      <code className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono font-medium">
                                        {fieldChange.newValue}
                                      </code>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <DialogFooter className="px-6 py-4 gap-2 sm:gap-0 flex-shrink-0 border-t">
            <Button
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={pendingChanges.length === 0}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              Confirm & Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <AlertDialog open={showCancelConfirmModal} onOpenChange={setShowCancelConfirmModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Discard Unsaved Changes?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to your asset configuration. If you cancel now, all modifications will be lost and the UI will be reset to the last saved state.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
